import { InfoPopup, Pagination, useMessageContext } from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import { PriorityTableRow } from '@src/components/orbitalTrack/rentalSetting/inviteRental/PriorityTableRow'
import ApeironConfirmButton from '@src/components/share/apeiron/ApeironConfirmButton'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import { useFullScreenBlockingContext } from '@src/contexts/share/FullScreenBlockingContext'
import useUpdateOrbitalTrackInviteSettings, {
  UpdateOrbitalTrackInviteSettingsInput
} from '@src/hooks/graphql/useUpdateOrbitalTrackInviteSettings'
import * as R from 'ramda'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Popup = styled(InfoPopup)`
  background: #171719;
  border-radius: unset;
  max-height: 96%;
  max-width: 700px;
  width: 96%;
  .Custom-Content {
    display: flex;
    flex-direction: column;
    margin: -50px 0px 0px 0px;
    width: 100%;
  }
`

const Head = styled(Box)`
  background-color: #262829;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    padding: 30px 70px 30px 30px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    padding: 20px 50px 20px 20px;
  }
`

const Title = styled(Typography)`
  color: white;
  font-size: 20px;
  font-weight: 500;
  line-height: 20px;
`

const Content = styled(Box)`
  padding: 30px 5px;
  ${props => props.theme.breakpoints.up('res768')} {
    padding: 30px;
  }
`

const ContentHeader = styled(Box)`
  background-color: black;
  color: white;
  width: 100%;
  padding: 12px 16px;
`

const PaginationContainer = styled(Box)`
  display: flex;
`

const TableContainer = styled(Box)`
  background: var(--linear, linear-gradient(157deg, #1e1e1e 3.12%, #000 100%));
  box-shadow: 0px -1px 0px 0px #27272a inset;
`

const BottomContainer = styled(Box)`
  display: flex;
  margin-top: 20px;
  justify-content: space-between;

  ${props => props.theme.breakpoints.down('res480')} {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
  }
`

const RowsContainer = styled(Box)`
  ${props => props.theme.breakpoints.down('res480')} {
    max-height: 50dvh;
    overflow-y: auto;
  }
`

const PAGE_SIZE = 6

export const PriorityEditDialog = (props: Props) => {
  const { open, className, onClose } = props

  const { t } = useTranslation()

  const { trackDetail, star, refetchStar } = useOrbitalTrackAttachContext()

  const [currentPage, setCurrentPage] = useState(0)

  const { fullScreenBlocking, discardFullScreenBlocking } =
    useFullScreenBlockingContext()

  const [tempInviteSettings, setTempInviteSettings] = useState(
    trackDetail?.inviteSettings || []
  )

  const { setInfo } = useMessageContext()

  const { mutate: updateInviteSetting, loading } =
    useUpdateOrbitalTrackInviteSettings()

  const handleOnClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleConfirmEdit = useCallback(async () => {
    const settingsToSubmit: UpdateOrbitalTrackInviteSettingsInput[] = []

    fullScreenBlocking()

    tempInviteSettings.forEach((setting, index) => {
      if (setting.priority !== index) {
        settingsToSubmit.push({
          inviteGroupID: setting.inviteGroupId,
          priority: index
        })
      }
    })

    const success = await updateInviteSetting({
      starID: star?.id || '',
      settings: settingsToSubmit
    })

    discardFullScreenBlocking()

    if (success) {
      refetchStar()
      onClose()
      setInfo(null)
    } else {
      setInfo({
        message: t('godiverse.orbital_track.priority_update_fail')
      })
    }
  }, [updateInviteSetting, star?.id, tempInviteSettings, onClose, refetchStar])

  const decrease1 = useCallback((fromIndex: number) => {
    setTempInviteSettings(tempInviteSettings =>
      R.move(fromIndex, Math.max(0, fromIndex - 1), tempInviteSettings)
    )
  }, [])

  const toTop = useCallback((fromIndex: number) => {
    setTempInviteSettings(tempInviteSettings =>
      R.move(fromIndex, 0, tempInviteSettings)
    )
  }, [])

  const increase1 = useCallback((fromIndex: number) => {
    setTempInviteSettings(tempInviteSettings =>
      R.move(
        fromIndex,
        Math.min(tempInviteSettings.length, fromIndex + 1),
        tempInviteSettings
      )
    )
  }, [])

  const toBottom = useCallback((fromIndex: number) => {
    setTempInviteSettings(tempInviteSettings =>
      R.move(fromIndex, tempInviteSettings.length - 1, tempInviteSettings)
    )
  }, [])

  const jump = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (toIndex < 0 || toIndex >= tempInviteSettings.length) return

      setTempInviteSettings(tempInviteSettings =>
        R.move(fromIndex, toIndex, tempInviteSettings)
      )
    },
    [tempInviteSettings.length]
  )

  const totalPage = Math.max(
    1,
    Math.ceil(tempInviteSettings.length / PAGE_SIZE)
  )

  const currentRows = tempInviteSettings.filter(
    (_, index) =>
      index >= currentPage * PAGE_SIZE && index < (currentPage + 1) * PAGE_SIZE
  )

  return (
    <Popup className={className} open={open} onClose={handleOnClose}>
      <Head>
        <Title>{t('godiverse.orbital_track.edit_priority')}</Title>
      </Head>
      <Content>
        <TableContainer>
          <ContentHeader>Priority</ContentHeader>
          <RowsContainer>
            {currentRows?.map((inviteSetting, index) => {
              const realIndex = currentPage * PAGE_SIZE + index

              return (
                <PriorityTableRow
                  key={inviteSetting.inviteGroupId}
                  inviteSetting={inviteSetting}
                  realIndex={realIndex}
                  onUp={() => decrease1(realIndex)}
                  onDown={() => increase1(realIndex)}
                  onTop={() => toTop(realIndex)}
                  onBottom={() => toBottom(realIndex)}
                  onJump={toIndex => jump(realIndex, toIndex)}
                />
              )
            })}
          </RowsContainer>
        </TableContainer>
        <BottomContainer>
          <PaginationContainer>
            {totalPage > 1 && (
              <Pagination
                totalPage={totalPage}
                value={currentPage + 1}
                onPrev={() => setCurrentPage(cur => cur - 1)}
                onNext={() => setCurrentPage(cur => cur + 1)}
                prevDisabled={currentPage === 0}
                nextDisabled={currentPage + 1 === totalPage}
                onSubmit={pageNumber => {
                  if (pageNumber <= totalPage && pageNumber >= 1) {
                    setCurrentPage(pageNumber - 1)
                  }
                }}
              />
            )}
          </PaginationContainer>
          <ApeironConfirmButton onClick={handleConfirmEdit} loading={loading} />
        </BottomContainer>
      </Content>
      <Box />
    </Popup>
  )
}

type Props = {
  open: boolean
  className?: string
  onClose: () => void
}
